import AddIcon from "@mui/icons-material/Add";
import MessageIcon from "@mui/icons-material/Message";
import ShareIcon from "@mui/icons-material/Share";
import { Theme } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Hit } from "_shared/_api/search/search-results";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import { MarketplaceResultType } from "config";
import { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import IndividualBookmarkButton from "marketplace/bookmarks/_views/individual-bookmark-button";
import ViewCompanyProfile from "marketplace/companies/profiles/_views/view-company-profile";
import {
  addIndividual,
  getContactIndividualForum,
  getIndividualAvatar,
  getIndividualAvatarError,
  getIndividualAvatarIsLoading,
  populateIndividualAvatar,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import MarketplaceIndividualInfoSchema, { MarketplaceIndividualInfoJSON } from "marketplace/_schema/marketplace-individual-info-schema";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import ChatWindow from "messaging/forums/_views/components/forum-chat-window";
import Forum from "messaging/forums/forum";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { NavigateFunction } from "react-router";
import Session from "users/__session/session";
import { selectIsNetworkedWith } from "users/networking/connections/_selectors/user-network-connection-selectors";
import NetworkButton from "users/networking/connections/_views/components/network-button";
import DirectProposalButton from "work/proposals/_views/_buttons/direct-proposal-button";

const ResultCard = styled(Card)(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    display: "block",
  },
  display: "flex",
  flex: "1 0 auto",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
  "&:first-of-type": {
    marginTop: 0,
  },
}));
const ResultHeader = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "grid",
  columnGap: theme.spacing(2),
  gridTemplateColumns: "auto 1fr",
}));
const ResultCardContent = styled(CardContent)(({ theme }) => ({
  flexGrow: "inherit",
  padding: theme.spacing(2),
  paddingLeft: theme.spacing(3),
}));
const ResultCardActions = styled(CardActions)(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    alignContent: "flex-end",
    alignItems: "unset",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down("lg")]: {
    alignContent: "flex-start",
    alignItems: "unset",
    flexDirection: "row",
    justifyContent: "flex-start",
    marginBottom: theme.spacing(2),
  },
  alignContent: "center",
  alignItems: "flex-end",
  flex: 0,
  flexDirection: "column",
  flexWrap: "wrap",
  justifyContent: "space-evenly",
  paddingRight: theme.spacing(3),
}));
const ResultName = styled(Typography)(() => ({
  cursor: "pointer",
  textDecoration: "underline",
  "& em": {
    backgroundColor: "rgba(255, 255, 0, 0.25)",
    fontStyle: "normal",
  },
}));
const TypeChip = styled(Chip)(({ theme }) => ({
  borderColor: "#33c",
  borderStyle: "dashed",
  color: "#33c",
  marginRight: theme.spacing(1),
  marginTop: theme.spacing(0.5),
  opacity: 0.66,
}));
const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(30),
}));
const StyledNetworkButton = styled(NetworkButton)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(30),
}));
const StyledBookmarkButton = styled(IndividualBookmarkButton)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(30),
}));
const Subtitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
}));
const ChatCardContent = styled(CardContent)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  width: "100%",
}));
const Details = styled("div")(({ theme }) => ({
  [theme.breakpoints.up(1920)]: {
    columnCount: 2,
  },
  [theme.breakpoints.down(1920)]: {
    columnCount: 1,
  },
  [theme.breakpoints.down("xl")]: {
    columnCount: 2,
  },
  [theme.breakpoints.down("lg")]: {
    columnCount: 2,
  },
  [theme.breakpoints.down("md")]: {
    columnCount: 1,
  },
  columnGap: theme.spacing(2),
  columnFill: "balance",
  marginTop: theme.spacing(2),
  "& em": {
    backgroundColor: "rgba(255, 255, 0, 0.25)",
    fontStyle: "normal",
  },
}));
const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.black,
  cursor: "pointer",
  textDecoration: "underline",
})) as typeof Link;
const SmallAvatar = styled(Avatar)(({ theme }) => ({
  cursor: "pointer",
  height: theme.spacing(10),
  width: theme.spacing(10),
}));
const ResultAvatarCardMedia = styled(CardMedia)(() => ({
  cursor: "pointer",
  height: "auto",
  width: "15vw",
})) as typeof CardMedia;
const ResultAvatar = styled(Avatar)(() => ({
  cursor: "pointer",
  height: "auto",
  width: "15vw",
}));
const GridRow = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  marginBottom: theme.spacing(1),
}));
const SelectMemberButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(2),
}));
const NameContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
}));

export enum TeamMemberType {
  Leader = "leader",
  Member = "member",
}

type IndividualResultProps = {
  selectingAsLeader?: boolean;
  selectedTeamMemberUserIds?: Guid[];
  hit: Hit<MarketplaceSearchInfoJSON>;
  result: MarketplaceIndividualInfoJSON;
  embedded: boolean;
  session: Session;
  navigate?: NavigateFunction;
  getText: (
    result: Hit<MarketplaceSearchInfoJSON>,
    fieldNames: string[],
    base?: unknown,
    noCommas?: boolean,
  ) => { __html: string | TrustedHTML };
  handleResultClicked: (
    event: React.MouseEvent,
    profileId: Guid,
    profileType: MarketplaceResultType,
    component: React.JSX.Element,
    fullWidth?: boolean,
  ) => void;
  onTeamMemberSelected?: (teamMember: Individual) => void;
};

export default function IndividualResult(props: Readonly<IndividualResultProps>) {
  const {
    hit,
    selectingAsLeader,
    selectedTeamMemberUserIds,
    result,
    embedded,
    session,
    navigate,
    getText,
    handleResultClicked,
    onTeamMemberSelected,
  } = props;

  const { firstName, lastName, id } = result;
  const individualId = React.useMemo(() => (id ? new Guid(id) : null), [id]);

  const [showChat, setShowChat] = React.useState<boolean>(false);
  const [forum, setForum] = React.useState<Forum>();
  const individual = MarketplaceIndividualInfoSchema.deserialize(result);

  const dispatch = useAttorneyHubDispatch();
  const isXlDownDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("xl"));
  const isXlUpDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.up("xl"));

  const isNetworked = useSelector(selectIsNetworkedWith(individual.userId));
  const avatar = useSelector(getIndividualAvatar(individualId));
  const isLoadingAvatar = useSelector(getIndividualAvatarIsLoading(individualId));
  const errorLoadingAvatar = useSelector(getIndividualAvatarError(individualId));

  useEffect(() => {
    if (!individualId || avatar || isLoadingAvatar || errorLoadingAvatar || !result.avatarId) return;
    dispatch(populateIndividualAvatar({ individualId }));
  }, [dispatch, session, individualId, avatar, isLoadingAvatar, errorLoadingAvatar, result.avatarId]);

  async function contactIndividual() {
    try {
      if (!result?.id) throw new Error("Individual ID is required to contact individual.");
      const forumResponse = await dispatch(
        getContactIndividualForum({
          individualId,
        }),
      ).unwrap();
      setForum(Forum.fromJSON(forumResponse));
      setShowChat(true);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't find user", { variant: "error" });
    }
  }

  function getAddButton(result: MarketplaceIndividualInfoJSON) {
    let tooltipTitle = "";
    let buttonLabel = "Add To Team";

    const alreadyOnTeam = selectedTeamMemberUserIds?.some((id) => id.isEqualTo(new Guid(result.userId!)));
    const isDisabled = (selectingAsLeader && !result.hasSigningAuthority) || alreadyOnTeam;
    const profileNotVisible = result.isVisible === false;

    if (profileNotVisible) {
      tooltipTitle = "Publish your profile to the marketplace to add to vendor-team";
    }
    if (alreadyOnTeam) {
      tooltipTitle = "Already on vendor-team";
      buttonLabel = "On Team";
    }

    return (
      <Tooltip title={tooltipTitle}>
        <span>
          <SelectMemberButton
            variant="outlined"
            color="primary"
            aria-label={buttonLabel}
            startIcon={<AddIcon />}
            disableElevation
            disabled={isDisabled}
            onClick={() => handleSelectMemberButtonClicked(result)}
          >
            {buttonLabel}
          </SelectMemberButton>
        </span>
      </Tooltip>
    );
  }

  function openIndividualProfile(event: React.MouseEvent, id: string | null | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Individual,
      <ViewIndividualProfile individualId={profileId} />,
      true,
    );
  }

  function openCompanyProfile(event: React.MouseEvent, id: string | null | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Company,
      <ViewCompanyProfile companyId={profileId} />,
      true,
    );
  }

  function handleSelectMemberButtonClicked(result: MarketplaceIndividualInfoJSON) {
    const individual = MarketplaceIndividualInfoSchema.deserialize(result);
    if (result.isVisible === false) {
      publishProfile(individual);
      return;
    }
    onTeamMemberSelected?.(individual);
  }

  function showProposalButtons() {
    // If user is not a representative, do not render
    if (!session.isContextRepresentative) return false;

    // If target is not a representative, do not render
    if (!result.hasSigningAuthority) return false;

    // Do not render button if result is self
    const resultUserId = result?.userId ? new Guid(result.userId) : undefined;
    if (!resultUserId || session.user?.id?.isEqualTo(resultUserId)) return false;

    const resultEntityId = result?.legalEntityId ? new Guid(result.legalEntityId) : undefined;
    if (!resultEntityId) return false;

    // Do not render button if user is same entity as target
    const userEntityId = session.user!.legalEntityId;
    return userEntityId?.isEqualTo(resultEntityId) === false;
  }

  function handleSendProposalToResult(targetUrl: URL): void {
    navigate?.(targetUrl.pathname + targetUrl.search);
  }

  function publishProfile(individual: Individual) {
    try {
      individual.toggleVisibility();
      dispatch(addIndividual({ session, individual }));
      enqueueSnackbar("Profile published", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't publish profile. Please try again", { variant: "error" });
    }
  }

  return (
    <React.Fragment>
      <ResultCard raised={false} variant="outlined">
        {isXlUpDisplaySize && (
          <>
            <ResultAvatarCardMedia
              style={{ display: avatar ? undefined : "none" }}
              component="img"
              image={avatar}
              onClick={(event) => openIndividualProfile(event, result?.id)}
            />
            <ResultAvatar
              style={{ display: avatar ? "none" : undefined }}
              variant="square"
              onClick={(event) => openIndividualProfile(event, result?.id)}
            />
          </>
        )}
        <ResultCardContent>
          <ResultHeader>
            {isXlDownDisplaySize && (
              <span>
                <SmallAvatar
                  style={{ display: avatar ? undefined : "none" }}
                  variant="circular"
                  src={avatar}
                  onClick={(event) => openIndividualProfile(event, result?.id)}
                />
                <SmallAvatar
                  style={{ display: avatar ? "none" : undefined }}
                  variant="circular"
                  onClick={(event) => openIndividualProfile(event, result?.id)}
                />
              </span>
            )}
            <span>
              <NameContainer>
                <ResultName
                  variant="h5"
                  dangerouslySetInnerHTML={getText(hit, ["firstName", "lastName"], result, true)}
                  onClick={(event) => openIndividualProfile(event, result?.id)}
                />
                {embedded && getAddButton(result)}
              </NameContainer>
              <TypeChip label="Individual" variant="outlined" size="small" />
            </span>
          </ResultHeader>
          <Details>
            {result?.companyName && (
              <GridRow>
                <Subtitle variant="body1">Company:</Subtitle>
                <StyledLink
                  component={Typography}
                  dangerouslySetInnerHTML={getText(hit, ["companyName"])}
                  onClick={(event) => openCompanyProfile(event, result?.companyId)}
                ></StyledLink>
              </GridRow>
            )}
            {result?.technicalBackground && (
              <GridRow>
                <Subtitle variant="body1">Technical Background:</Subtitle>
                <Typography dangerouslySetInnerHTML={getText(hit, ["technicalBackground"])} />
              </GridRow>
            )}
            {result?.barAdmissionYear && (
              <GridRow>
                <Subtitle variant="body1">Bar Admission Year:</Subtitle>
                <Typography dangerouslySetInnerHTML={getText(hit, ["barAdmissionYear"])} />
              </GridRow>
            )}
            {result?.location && (
              <GridRow>
                <Subtitle variant="body1">Location:</Subtitle>
                <Typography dangerouslySetInnerHTML={getText(hit, ["location"])} />
              </GridRow>
            )}
            {result?.billingRate && result.billingRate !== "" && (
              <GridRow>
                <Subtitle variant="body1">Billing Rate:</Subtitle>
                <Box sx={{ display: "flex" }}>
                  <Typography dangerouslySetInnerHTML={getText(hit, ["billingRate"])} />
                  {result.billingUnit && result.billingUnit !== "" && (
                    <>
                      <Typography>&nbsp;/&nbsp;</Typography>
                      <Typography dangerouslySetInnerHTML={getText(hit, ["billingUnit"])} />
                    </>
                  )}
                </Box>
              </GridRow>
            )}
            {result?.regionsRegistered && result?.regionsRegistered?.length > 0 && (
              <GridRow>
                <Subtitle variant="body1">Regions Registered In:</Subtitle>
                <Typography dangerouslySetInnerHTML={getText(hit, ["regionsRegistered"])} />
              </GridRow>
            )}
          </Details>
        </ResultCardContent>
        <ResultCardActions disableSpacing>
          {!embedded && (
            <>
              <StyledNetworkButton userId={individual.userId} />
              <StyledBookmarkButton individualId={individual.id} />
              <Tooltip title={isNetworked ? "" : "You must be in a individual's network before contacting them"}>
                <div style={{ display: "contents", width: "100%" }}>
                  <StyledButton
                    startIcon={<MessageIcon />}
                    variant="outlined"
                    disabled={!isNetworked}
                    onClick={contactIndividual}
                  >
                    Send Message
                  </StyledButton>
                </div>
              </Tooltip>
              <StyledButton
                aria-label="share"
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={() => {
                  navigator.clipboard
                    .writeText(`${globalThis.location.origin}/marketplace/individual/${result?.id}`)
                    .then(() => {
                      enqueueSnackbar("Link copied to clipboard", { variant: "info" });
                    })
                    .catch(() => {
                      enqueueSnackbar("Couldn't copy link to clipboard", { variant: "error" });
                    });
                }}
              >
                Share
              </StyledButton>
              {showProposalButtons() && (
                <>
                  <DirectProposalButton
                    targetUserId={new Guid(result.userId!)}
                    targetEntityId={new Guid(result.legalEntityId!)}
                    targetName={Name.create({ params: { firstName, lastName } })}
                    targethasSigningAuthority={result.hasSigningAuthority ?? undefined}
                    onClick={handleSendProposalToResult}
                    useStyledButton={true}
                    action={"create"}
                  />
                  <DirectProposalButton
                    targetUserId={new Guid(result.userId!)}
                    targetEntityId={new Guid(result.legalEntityId!)}
                    targetName={Name.create({ params: { firstName: firstName ?? undefined, lastName: lastName ?? undefined } })}
                    targethasSigningAuthority={result.hasSigningAuthority ?? undefined}
                    onClick={handleSendProposalToResult}
                    useStyledButton={true}
                    action={"request"}
                  />
                </>
              )}
            </>
          )}
        </ResultCardActions>
      </ResultCard>
      {showChat && (
        <ChatCardContent>
          <ChatWindow forums={forum ? [forum] : []} onClose={() => setShowChat(false)} />
        </ChatCardContent>
      )}
    </React.Fragment>
  );
}
